import { fieldSurface } from '../field-focus'
import { labelContent } from './labelContent'
import { useId, useState } from 'react'

interface TextInputProps {
  label?: string
  /** Use when the caller renders its own visible label (for example with a help icon). */
  ariaLabel?: string
  id?: string
  size?: 'large' | 'small'
  inputType?: 'text' | 'email' | 'password'
  placeholder?: string
  value: string
  onChange: (value: string) => void
  /** Runs when the field loses focus, for callers that tidy a value once it is finished. */
  onBlur?: () => void
  error?: string
  disabled?: boolean
  className?: string
}

const SIZE: Record<'large' | 'small', { height: number; px: number }> = {
  large: { height: 40, px: 12 },
  small: { height: 32, px: 10 },
}

export function TextInput({
  label,
  size = 'large',
  inputType = 'text',
  placeholder,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  className = '',
  id,
  ariaLabel,
}: TextInputProps) {
  const generatedId = useId()
  const inputId = id ?? generatedId
  const errorId = `${inputId}-error`
  const [focused, setFocused] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const isPassword = inputType === 'password'
  const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : inputType

  const { borderColor, backgroundColor } = fieldSurface({ focused, error: Boolean(error) })
  const { height, px } = SIZE[size]

  return (
    <div className={`flex flex-col ${className}`} style={{ gap: 8 }}>
      {label && (
        <label htmlFor={inputId} className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
          {labelContent(label)}
        </label>
      )}

      <div
        className="flex items-center"
        style={{
          height,
          backgroundColor,
          border: `0.5px solid ${borderColor}`,
          paddingLeft: px,
          paddingRight: px,
          gap: 8,
          opacity: disabled ? 0.6 : 1,
          transition: 'background-color 0.15s ease',
        }}
      >
        <input
          id={inputId}
          {...(!label && ariaLabel ? { 'aria-label': ariaLabel } : {})}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          type={resolvedType}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); onBlur?.() }}
          className="flex-1 min-w-0 bg-transparent font-jakarta text-text-input placeholder:text-text-description outline-none"
          style={{ fontSize: 12, lineHeight: '16px', letterSpacing: '-0.12px', caretColor: '#FFFFFF' }}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(s => !s)}
            className="font-jakarta text-text-support hover:text-white transition-colors flex-shrink-0"
            style={{ fontSize: 12, lineHeight: '16px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        )}
      </div>

      {error && (
        <span id={errorId} role="alert" className="font-mulish" style={{ fontSize: 10, lineHeight: '12px', color: '#F05042' }}>
          {error}
        </span>
      )}
    </div>
  )
}
